/* ==========================================================================
   Baiby - theme.js
   No dependencies. Every module is opt-in via a data-attribute, so a section
   that is removed in the customizer simply leaves its module idle.
   ========================================================================== */
(function () {
  'use strict';

  var strings = window.BaibyStrings || {};
  var routes = window.BaibyRoutes || {};

  // Pause between a successful add and the drawer starting to open. Combined
  // with --drawer-open-ms in the CSS this is the ~1s hand-off.
  var CART_OPEN_DELAY = 340;

  // How long after the drawer starts opening before the free shipping sparkles
  // fire. Roughly two thirds through the 600ms open, so the box has arrived but
  // the motion still feels connected to it.
  var SHIP_CELEBRATE_DELAY = 400;

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* Mirrors Shopify's money filter. Every placeholder Shopify supports is
     handled: miss one and the raw "{{amount_...}}" string is printed to the
     customer, which is what happened with this store's space separator.

     Each placeholder implies its own thousands and decimal separators. */
  var MONEY_FORMATS = {
    amount:                                  { thousands: ',', decimal: '.', decimals: 2 },
    amount_no_decimals:                      { thousands: ',', decimal: '.', decimals: 0 },
    amount_with_comma_separator:             { thousands: '.', decimal: ',', decimals: 2 },
    amount_no_decimals_with_comma_separator: { thousands: '.', decimal: ',', decimals: 0 },
    amount_with_space_separator:             { thousands: ' ', decimal: ',', decimals: 2 },
    amount_no_decimals_with_space_separator: { thousands: ' ', decimal: ',', decimals: 0 },
    amount_with_apostrophe_separator:        { thousands: "'", decimal: '.', decimals: 2 }
  };

  function money(cents) {
    var format = window.BaibyMoneyFormat || '{{amount_with_space_separator}} kr';
    var amount = (parseInt(cents, 10) || 0) / 100;

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      var rule = MONEY_FORMATS[name];
      if (!rule) return match;
      var fixed = amount.toFixed(rule.decimals);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, rule.thousands);
      return rule.decimals && parts[1] ? whole + rule.decimal + parts[1] : whole;
    });
  }

  function announce(message) {
    var region = $('[data-sr-live]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(function () { region.textContent = message; }, 60);
  }

  /* ------------------------------------------------------- header height --
     One measured value feeds --header-h, which every sticky offset and
     scroll-padding rule in the CSS reads. Keeps content clear of the nav on
     every page instead of per-section guesswork. */
  var header = $('[data-header]');

  function syncHeaderHeight() {
    if (!header) return;
    var h = Math.round(header.getBoundingClientRect().height);
    if (h > 0) document.documentElement.style.setProperty('--header-h', h + 'px');
  }

  if (header) {
    syncHeaderHeight();
    if ('ResizeObserver' in window) {
      new ResizeObserver(syncHeaderHeight).observe(header);
    } else {
      window.addEventListener('resize', syncHeaderHeight);
    }
    window.addEventListener('load', syncHeaderHeight);

    // Shadow the header once the page has scrolled past the announcement bar.
    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;';
    document.body.insertBefore(sentinel, document.body.firstChild);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        header.classList.toggle('is-stuck', !entries[0].isIntersecting);
      }).observe(sentinel);
    }
  }

  /* -------------------------------------------------------------- drawers */
  var overlay = $('[data-drawer-overlay]');
  var openDrawer = null;
  var lastFocused = null;

  function focusables(el) {
    return $$('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', el)
      .filter(function (n) { return n.offsetParent !== null; });
  }

  function drawerOpen(name) {
    var drawer = $('[data-drawer="' + name + '"]');
    if (!drawer) return;
    if (openDrawer && openDrawer !== drawer) drawerClose();
    lastFocused = document.activeElement;
    openDrawer = drawer;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.classList.add('is-open');
    document.body.classList.add('is-locked');
    $$('[data-drawer-open="' + name + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    var first = focusables(drawer)[0];
    if (first) window.setTimeout(function () { first.focus(); }, 60);
  }

  function drawerClose() {
    if (!openDrawer) return;
    var name = openDrawer.getAttribute('data-drawer');
    openDrawer.classList.remove('is-open');
    openDrawer.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.classList.remove('is-open');
    document.body.classList.remove('is-locked');
    $$('[data-drawer-open="' + name + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    openDrawer = null;
    if (lastFocused && lastFocused.focus) lastFocused.focus();
    lastFocused = null;
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      drawerOpen(opener.getAttribute('data-drawer-open'));
      return;
    }
    if (e.target.closest('[data-drawer-close]')) {
      e.preventDefault();
      drawerClose();
      return;
    }
    if (overlay && e.target === overlay) drawerClose();
  });

  document.addEventListener('keydown', function (e) {
    if (!openDrawer) return;
    if (e.key === 'Escape') { drawerClose(); return; }
    if (e.key !== 'Tab') return;
    var items = focusables(openDrawer);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* --------------------------------------------------------- announcement */
  $$('[data-announcement]').forEach(function (bar) {
    var items = $$('.announcement__item', bar);
    if (items.length < 2) return;
    var delay = parseInt(bar.getAttribute('data-interval'), 10) || 5000;
    var i = 0;
    window.setInterval(function () {
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, delay);
  });

  /* ------------------------------------------------------------- accordion */
  /* Height is animated between 0 and the measured scrollHeight, then handed
     back to `auto` so the panel reflows if its content changes later. The
     `hidden` attribute is only present while fully closed, which keeps
     collapsed content out of the tab order and the accessibility tree. */
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function panelSettle(panel, handler) {
    panel.addEventListener('transitionend', function done(e) {
      if (e.target !== panel || e.propertyName !== 'height') return;
      panel.removeEventListener('transitionend', done);
      handler();
    });
  }

  function openAccordion(panel) {
    panel.hidden = false;
    if (reduceMotion.matches) {
      panel.classList.add('is-open');
      panel.style.height = '';
      return;
    }
    panel.style.height = '0px';
    panel.offsetHeight;                       // flush the start value
    panel.classList.add('is-open');
    panel.style.height = panel.scrollHeight + 'px';
    panelSettle(panel, function () {
      if (panel.classList.contains('is-open')) panel.style.height = 'auto';
    });
  }

  function closeAccordion(panel) {
    if (reduceMotion.matches) {
      panel.classList.remove('is-open');
      panel.style.height = '';
      panel.hidden = true;
      return;
    }
    panel.style.height = panel.scrollHeight + 'px';
    panel.offsetHeight;                       // flush before collapsing
    panel.classList.remove('is-open');
    panel.style.height = '0px';
    panelSettle(panel, function () {
      if (!panel.classList.contains('is-open')) {
        panel.hidden = true;
        panel.style.height = '';
      }
    });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-accordion-trigger]');
    if (!trigger) return;
    var panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;
    var expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!expanded));
    if (expanded) closeAccordion(panel);
    else openAccordion(panel);
  });

  /* ------------------------------------------------------ quantity steppers */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    var wrap = btn.closest('[data-qty]');
    var input = $('input', wrap);
    if (!input) return;
    var step = parseInt(btn.getAttribute('data-qty-step'), 10);
    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) min = 0;
    var max = parseInt(input.getAttribute('max'), 10);
    var next = (parseInt(input.value, 10) || min) + step;
    if (next < min) next = min;
    if (!isNaN(max) && next > max) next = max;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* -------------------------------------------------------------- the cart */
  function cartCountFrom(html) {
    var m = html.match(/data-cart-count="(\d+)"/);
    return m ? parseInt(m[1], 10) : null;
  }

  function paintCartCount(count) {
    if (count === null || count === undefined) return;
    $$('[data-cart-badge]').forEach(function (badge) {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
    $$('[data-cart-toggle]').forEach(function (el) {
      el.setAttribute('aria-label', (strings.cartLabel || 'Kundvagn') + ' (' + count + ')');
    });
  }

  function replaceSection(id, html) {
    var target = document.getElementById(id);
    if (!target) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.getElementById(id);
    if (fresh) target.innerHTML = fresh.innerHTML;
  }

  /* ------------------------------------------------ free shipping meter --
     The cart sections are replaced wholesale, so each new bar arrives already
     painted at its final width and no transition would ever run. Read the
     outgoing bar's value first, snap the incoming one back to it for a frame,
     then release it so the width animates from old to new. */
  function shipState() {
    var fill = $('[data-ship-fill]');
    var meter = $('[data-ship-meter]');
    return {
      percent: fill ? parseFloat(fill.getAttribute('data-ship-percent')) : null,
      unlocked: meter ? meter.getAttribute('data-ship-unlocked') === 'true' : null
    };
  }

  // Set when the threshold is crossed, cleared once the sparkles have played.
  // The celebration has to wait for the drawer to be on screen, otherwise it
  // runs during the 340ms pause before the drawer even starts opening and the
  // customer never sees it.
  var shipCelebratePending = false;

  function runShipCelebration() {
    if (!shipCelebratePending) return;
    var meters = $$('[data-ship-meter]');
    if (!meters.length) return;
    shipCelebratePending = false;
    meters.forEach(function (meter) { meter.classList.add('just-unlocked'); });
  }

  function animateShipMeter(previous) {
    $$('[data-ship-meter]').forEach(function (meter) {
      var nowUnlocked = meter.getAttribute('data-ship-unlocked') === 'true';

      // Celebrate the crossing only, not every later change while unlocked.
      if (nowUnlocked && previous.unlocked === false) {
        shipCelebratePending = true;
      }

      var fill = $('[data-ship-fill]', meter);
      if (!fill || previous.percent === null) return;
      var target = parseFloat(fill.getAttribute('data-ship-percent'));
      if (isNaN(target) || target === previous.percent) return;

      fill.classList.add('is-resetting');
      fill.style.width = previous.percent + '%';
      fill.offsetHeight;                        // commit the rewind un-animated
      fill.classList.remove('is-resetting');
      window.requestAnimationFrame(function () {
        fill.style.width = target + '%';
      });
    });
  }

  function applySections(sections) {
    if (!sections) return;
    // Replacing a section's innerHTML destroys the drawer element inside it, so
    // remember which drawer was open and re-apply the state to the new node.
    var reopen = openDrawer ? openDrawer.getAttribute('data-drawer') : null;
    var previousShip = shipState();

    Object.keys(sections).forEach(function (key) {
      var html = sections[key];
      var count = cartCountFrom(html);
      if (count !== null) paintCartCount(count);
      replaceSection('shopify-section-' + key, html);
    });

    if (reopen) {
      var fresh = $('[data-drawer="' + reopen + '"]');
      openDrawer = fresh || null;
      if (fresh) {
        fresh.classList.add('is-open');
        fresh.setAttribute('aria-hidden', 'false');
        if (overlay) overlay.classList.add('is-open');
        document.body.classList.add('is-locked');
      } else {
        if (overlay) overlay.classList.remove('is-open');
        document.body.classList.remove('is-locked');
      }
    }

    animateShipMeter(previousShip);
    enforceGifts();

    // Already on screen? Celebrate now. Otherwise the add-to-cart handler
    // fires it once the drawer has arrived.
    var cartVisible = (openDrawer && openDrawer.getAttribute('data-drawer') === 'cart') ||
                      document.getElementById('shopify-section-main-cart');
    if (cartVisible) runShipCelebration();
  }

  // Section IDs differ between a section rendered from layout ("cart-drawer")
  // and one inside a JSON template ("template--123__main"), so they are read
  // off the DOM rather than guessed.
  function sectionsToRender() {
    return $$('[data-cart-section]')
      .map(function (el) { return el.getAttribute('data-cart-section'); })
      .filter(Boolean)
      .join(',');
  }

  function cartError(message) {
    announce(message);
    var box = $('[data-cart-error]');
    if (box) {
      box.textContent = message;
      box.hidden = false;
      window.setTimeout(function () { box.hidden = true; }, 6000);
    } else {
      window.alert(message);
    }
  }

  // Add to cart from any form marked data-cart-form (cards, PDP, cart suggestions).
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-cart-form]');
    if (!form) return;
    e.preventDefault();

    var button = $('[type="submit"]', form);
    if (button) { button.classList.add('is-loading'); button.disabled = true; }
    function resetButton() {
      if (button) { button.classList.remove('is-loading'); button.disabled = false; }
    }

    var toRender = sectionsToRender();

    /* A tier or a package option can put more than one line in the cart.
       cart/add takes an items array for that, so the request switches from a
       form post to JSON when there is more than the product itself, keeping it
       a single round trip rather than one call per item. */
    /* Only a form inside a product section can be a bundle, because tiers and
       packages live in that section's buy box. A card's form, in the grid or
       among the cart's suggestions, adds its own product and nothing else.

       This used to fall back to the whole document when the form belonged to
       no product section, and $() treats a null context as the document too,
       so a suggestion added from the drawer found the tier chosen on the
       product page behind it and put that in the cart instead. Adding a
       nappfodral from the drawer on the puff's page added a second puff. */
    var scope = form.closest('[data-product]');
    var idField = $('[name="id"]', form);
    var items = null;

    /* A quantity tier settles both how many of the product go in and which
       gifts ride along. A gift goes in once, not once per unit, so it cannot
       share the tier's quantity. */
    var tier = scope ? $('[data-tiers] [data-tier]:checked', scope) : null;
    if (tier) {
      var tierQty = parseInt(tier.getAttribute('data-tier-qty'), 10) || 1;

      /* With a picker per unit the bundle is not one line of N, it is N lines
         of one, each with whatever that unit was set to. Two units set to the
         same thing come back as one line of two, since Shopify folds identical
         lines together, and the customer still gets what they picked.

         Only the chosen tier's own pickers count: every tier carries its own
         set, and the ones belonging to tiers not chosen are along for the
         ride. */
      var card = tier.closest('[data-tier-card]') || scope;
      var units = $$('[data-unit-group]', card);
      if (units.length) {
        items = [];
        units.forEach(function (group) {
          var picked = $('input:checked', group);
          var id = picked && picked.getAttribute('data-unit-variant');
          if (id) items.push({ id: id, quantity: 1 });
        });
      }
      if (!items || !items.length) {
        items = [{ id: idField.value, quantity: tierQty }];
      }

      /* Each gift goes in carrying what it depends on: the product and how
         many of it the gift is earned by. The cart drawer is a global section
         and cannot see the tier config, so the rule has to travel on the line.
         Underscored, so Shopify keeps it out of the customer's view. */
      var bundleOf = $('[data-tiers]', scope);
      var bundleHandle = bundleOf ? bundleOf.getAttribute('data-bundle-product') : '';
      (tier.getAttribute('data-tier-gifts') || '').split(',').filter(Boolean)
        .forEach(function (spec) {
          var bits = spec.split(':');
          var line = { id: bits[0], quantity: 1 };
          if (bits[1] && bundleHandle) {
            line.properties = { _paket: bundleHandle, _paket_min: bits[1] };
            // Where the cart should send someone who taps the gift's name: the
            // product they can actually buy, not the 0 kr article.
            if (bits[2]) line.properties._paket_kop = bits[2];
          }
          items.push(line);
        });
    } else if (scope) {
      var pkg = $('[data-package-option]:checked', form) ||
                $('[data-packages] [data-package-option]:checked', scope);
      var extras = pkg ? (pkg.getAttribute('data-package-extra') || '').split(',').filter(Boolean) : [];
      if (extras.length) {
        var qtyField = $('[name="quantity"]', form);
        var qty = parseInt(qtyField && qtyField.value, 10) || 1;
        items = [{ id: idField.value, quantity: qty }];
        extras.forEach(function (id) { items.push({ id: id, quantity: qty }); });
      }
    }

    var request;
    if (items) {
      var payload = { items: items };
      if (toRender) payload.sections = toRender;
      request = fetch((routes.cartAdd || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(payload)
      });
    } else {
      var body = new FormData(form);
      if (toRender) body.append('sections', toRender);
      request = fetch((routes.cartAdd || '/cart/add') + '.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: body
      });
    }

    request
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) {
          resetButton();
          cartError(res.data.description || res.data.message || strings.cartError || 'Något gick fel.');
          return;
        }
        applySections(res.data.sections);
        announce(strings.addedToCart || 'Produkten lades i kundvagnen.');

        // Hold the button's loading state through a short beat, then open the
        // drawer. That pause plus the 600ms open transition puts the drawer
        // fully open about a second after the tap, long enough to read as a
        // deliberate hand-off rather than a flash.
        if (!document.getElementById('shopify-section-cart-drawer')) {
          resetButton();
          return;
        }
        window.setTimeout(function () {
          resetButton();
          drawerOpen('cart');
          window.setTimeout(runShipCelebration, SHIP_CELEBRATE_DELAY);
        }, CART_OPEN_DELAY);
      })
      .catch(function () {
        resetButton();
        cartError(strings.cartError || 'Något gick fel. Försök igen.');
      });
  });

  /* Each tier holds its own pickers and CSS shows only the chosen tier's, so
     nothing here has to hide anything. What does have to move is the option
     index: the variant sync reads every group carrying it and would see three
     answers to a one-option product, so exactly one group may hold it, and it
     belongs to the first unit of whichever tier is chosen. */
  function syncUnits(root, animate) {
    var cards = $$('[data-tier-card]', root);
    if (!cards.length) return;

    var lead = null;
    cards.forEach(function (card) {
      var input = $('[data-tier]', card);
      var open = !!(input && input.checked);
      if (open && !lead) lead = $('[data-unit-group]', card);

      var panel = $('[data-tier-units]', card);
      if (!panel) return;
      // Already where it should be, which is the case on first run: the page
      // is rendered settled, so nothing should animate into place on load.
      if (!panel.hidden === open) return;

      if (!animate) {
        panel.hidden = !open;
        panel.classList.toggle('is-open', open);
        panel.style.height = '';
      } else if (open) {
        openAccordion(panel);
      } else {
        closeAccordion(panel);
      }
    });

    $$('[data-unit-group]', root).forEach(function (group) {
      if (group === lead) group.setAttribute('data-option-index', '0');
      else group.removeAttribute('data-option-index');
    });

    // Hand the sync the new leader's choice, so the gallery and the form's
    // variant id follow the tier rather than staying on the old tier's pick.
    // Flagged, because this is the theme talking to itself: a tick on a tab
    // has to mean the customer chose, not that a default was handed along.
    var picked = lead ? $('input:checked', lead) : null;
    if (picked) {
      handingOver = true;
      picked.dispatchEvent(new Event('change', { bubbles: true }));
      handingOver = false;
    }
  }
  var handingOver = false;

  document.addEventListener('change', function (e) {
    /* Keep the form's quantity field in step with the chosen tier, so the DOM
       says the same thing the payload will and a post without JavaScript still
       asks for the right number. */
    var tier = e.target.closest('[data-tier]');
    if (tier && tier.checked) {
      var root = tier.closest('[data-product]') || document;
      var field = $('[data-cart-form] [name="quantity"]', root);
      if (field) field.value = tier.getAttribute('data-tier-qty') || '1';
      syncUnits(root, true);
      return;
    }

    // The first unit's label is written by the variant sync; the rest are on
    // their own, so every unit group updates its own readout here.
    var group = e.target.closest('[data-unit-group]');
    if (group && e.target.checked) {
      var out = $('[data-option-value]', group);
      if (out) out.textContent = e.target.value;
      if (!handingOver) markTab(group, e.target);
    }
  });

  /* Carry the choice up onto the tab: the picture so the other units stay
     readable while closed, and a tick so it is clear which ones have actually
     been dealt with rather than left on their default. */
  function markTab(group, input) {
    var card = group.closest('[data-tier-card]');
    if (!card) return;
    var tabs = $$('[data-unit-tab]', card);
    if (!tabs.length) return;
    var tab = tabs[$$('[data-unit-group]', card).indexOf(group)];
    if (!tab) return;

    tab.classList.add('is-done');
    var from = $('img', input.nextElementSibling);
    var thumb = $('.units__tab-thumb img', tab);
    if (from && thumb) {
      thumb.src = from.getAttribute('src');
      thumb.removeAttribute('srcset');
    }
  }

  function showUnit(tab) {
    var card = tab.closest('[data-tier-card]');
    if (!card) return;
    var tabs = $$('[data-unit-tab]', card);
    var panels = $$('[data-unit-group]', card);
    var at = tabs.indexOf(tab);
    tabs.forEach(function (t, i) {
      var on = i === at;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.setAttribute('tabindex', on ? '0' : '-1');
    });
    panels.forEach(function (p, i) { p.classList.toggle('is-active', i === at); });
  }

  document.addEventListener('click', function (e) {
    var tab = e.target.closest('[data-unit-tab]');
    if (tab) showUnit(tab);
  });

  // Arrow keys move between tabs, which is what a tablist is expected to do.
  document.addEventListener('keydown', function (e) {
    var tab = e.target.closest('[data-unit-tab]');
    if (!tab) return;
    var step = e.key === 'ArrowRight' ? 1 : (e.key === 'ArrowLeft' ? -1 : 0);
    if (!step) return;
    e.preventDefault();
    var tabs = $$('[data-unit-tab]', tab.closest('[data-tier-card]'));
    var next = tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length];
    showUnit(next);
    next.focus();
  });



  /* A gift only belongs in the cart while the thing that earns it is still
     there. The controls are gone from those lines, but a cart can be changed
     from more than one tab, and someone can always come back to an old one, so
     the rule is checked against what is actually in the cart rather than
     trusted to the buttons.

     One correction per pass: the change re-renders the cart, which runs this
     again, so a cart with several wrong lines settles in a few passes. */
  var fixingGifts = false;
  function enforceGifts() {
    if (fixingGifts) return;

    /* Counted inside one cart surface at a time. On the cart page the drawer
       is in the layout as well, so both render the same cart, and counting
       across the document sees every line twice: two puffs read as four and a
       gift that is no longer earned looks earned. */
    var surfaces = $$('[data-cart-section]');
    for (var s = 0; s < surfaces.length; s++) {
      if (correctGifts(surfaces[s])) return;
    }
  }

  function correctGifts(surface) {
    var gifts = $$('[data-gift-for]', surface);
    if (!gifts.length) return false;

    var have = {};
    $$('[data-cart-line]', surface).forEach(function (line) {
      if (line.hasAttribute('data-gift-for')) return;
      var handle = line.getAttribute('data-line-handle');
      if (!handle) return;
      have[handle] = (have[handle] || 0) + (parseInt(line.getAttribute('data-line-qty'), 10) || 0);
    });

    for (var i = 0; i < gifts.length; i++) {
      var line = gifts[i];
      var key = line.getAttribute('data-line-key');
      var need = parseInt(line.getAttribute('data-gift-min'), 10) || 1;
      var qty = parseInt(line.getAttribute('data-line-qty'), 10) || 0;
      if (!key) continue;

      if ((have[line.getAttribute('data-gift-for')] || 0) < need) {
        fixingGifts = true;
        changeLine(key, 0, strings.giftRemoved);
        return true;
      }
      // Never more than the one the bundle grants, whatever put it there.
      if (qty > 1) {
        fixingGifts = true;
        changeLine(key, 1);
        return true;
      }
    }
    return false;
  }

  function cartNote(message) {
    if (!message) return;
    announce(message);
    var box = $('[data-cart-error]');
    if (!box) return;
    box.textContent = message;
    box.classList.remove('form-message--error');
    box.classList.add('form-message--info');
    box.hidden = false;
    window.setTimeout(function () { box.hidden = true; }, 6000);
  }

  function changeLine(key, quantity, note) {
    var payload = { id: key, quantity: quantity };
    var toRender = sectionsToRender();
    if (toRender) payload.sections = toRender;

    fetch((routes.cartChange || '/cart/change') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        fixingGifts = false;
        applySections(data.sections);
        paintCartCount(data.item_count);
        if (note) cartNote(note);
        else announce(strings.cartUpdated || 'Kundvagnen uppdaterades.');
      })
      .catch(function () {
        fixingGifts = false;
        cartError(strings.cartError || 'Något gick fel. Försök igen.');
      });
  }

  document.addEventListener('click', function (e) {
    var remove = e.target.closest('[data-cart-remove]');
    if (!remove) return;
    e.preventDefault();
    changeLine(remove.getAttribute('data-cart-remove'), 0);
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-qty]');
    if (!input) return;
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    changeLine(input.getAttribute('data-cart-qty'), qty);
  });

  /* --------------------------------------------------------- product page */
  $$('[data-product]').forEach(function (root) {
    var dataEl = $('[data-product-json]', root);
    if (!dataEl) return;
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }

    var form = $('[data-cart-form]', root);
    /* Inside the form specifically. A bare [data-variant-id] would take the
       first one in the section, and anything above the form carrying that
       attribute would have its own value overwritten with the variant id. */
    var idInput = $('[data-cart-form] [data-variant-id]', root);
    var priceBox = $('[data-price-box]', root);
    var submit = $('[data-add-button]', root);
    var stickyPrice = $('[data-sticky-price]');

    function selectedOptions() {
      return $$('[data-option-index]', root).map(function (group) {
        var checked = $('input:checked', group);
        return checked ? checked.value : null;
      });
    }

    function matchVariant(opts) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var hit = true;
        for (var j = 0; j < opts.length; j++) {
          if (opts[j] !== null && v.options[j] !== opts[j]) { hit = false; break; }
        }
        if (hit) return v;
      }
      return null;
    }

    function render(variant) {
      if (!variant) {
        if (submit) {
          submit.disabled = true;
          $('.btn__label', submit).textContent = strings.unavailable || 'Ej tillgänglig';
        }
        return;
      }
      if (idInput) idInput.value = variant.id;

      if (priceBox) {
        var html = '<span class="price__current">' + money(variant.price) + '</span>';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          html += '<span class="price__was">' + money(variant.compare_at_price) + '</span>';
        }
        priceBox.innerHTML = html;
      }
      if (stickyPrice) stickyPrice.textContent = money(variant.price);

      // Klarna prices the instalment from this attribute, so it has to follow
      // the selected variant or it quotes the wrong monthly figure.
      var klarna = $('[data-klarna-amount]', root);
      if (klarna) klarna.setAttribute('data-purchase-amount', variant.price);

      // Same for the static monthly figure. Rounded up to match the Liquid, so
      // switching variant can never show a number below the real instalment.
      var instalment = $('[data-instalment-amount]', root);
      if (instalment) {
        var host = instalment.closest('[data-instalment-months]');
        var months = host ? parseInt(host.getAttribute('data-instalment-months'), 10) : 3;
        if (!months || months < 1) months = 3;
        instalment.textContent = money(Math.ceil(variant.price / months));
      }

      if (submit) {
        submit.disabled = !variant.available;
        var label = $('.btn__label', submit);
        if (label) label.textContent = variant.available ? (strings.addToCart || 'Lägg i kundvagn') : (strings.soldOut || 'Slutsåld');
      }


      // Reflect the variant in the URL so a shared link opens the same choice.
      if (window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      if (variant.featured_media_position) showSlide(root, variant.featured_media_position - 1);
    }

    root.addEventListener('change', function (e) {
      if (!e.target.closest('[data-option-index]')) return;
      var opts = selectedOptions();
      $$('[data-option-index]', root).forEach(function (group) {
        var idx = parseInt(group.getAttribute('data-option-index'), 10);
        var label = $('[data-option-value]', group);
        if (label && opts[idx]) label.textContent = opts[idx];
        $$('input', group).forEach(function (input) {
          var probe = opts.slice();
          probe[idx] = input.value;
          var candidate = matchVariant(probe);
          input.disabled = !candidate;
          if (candidate && !candidate.available) input.parentNode.classList.add('is-soldout');
          else input.parentNode.classList.remove('is-soldout');
        });
      });
      render(matchVariant(opts));
    });

    // Mobile sticky buy bar appears once the real button leaves the viewport.
    var sticky = $('[data-sticky-atc]');
    if (sticky && submit && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        sticky.classList.toggle('is-visible', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
      }, { rootMargin: '0px 0px -80% 0px' }).observe(submit);
      $('[data-sticky-submit]', sticky) && $('[data-sticky-submit]', sticky).addEventListener('click', function () {
        if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    }
  });

  /* ------------------------------------------------------ product gallery */
  function showSlide(root, index) {
    var gallery = $('[data-gallery]', root) || $('[data-gallery]');
    if (!gallery) return;
    var slides = $$('.gallery__slide', gallery);
    if (!slides[index]) return;
    var desktop = window.matchMedia('(min-width: 990px)').matches;
    if (desktop) {
      slides.forEach(function (s, i) { s.classList.toggle('is-active', i === index); });
    } else {
      /* The gallery's own scroller, never scrollIntoView. That walks up to
         every scrollable ancestor including the page, so picking a tier or a
         model further down threw the reader back up to the photo. The image
         still changes underneath; they can scroll up to it if they want to
         look. */
      var shift = slides[index].getBoundingClientRect().left - gallery.getBoundingClientRect().left;
      if (Math.abs(shift) > 1) {
        var to = gallery.scrollLeft + shift;
        if (gallery.scrollTo) {
          gallery.scrollTo({ left: to, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        } else {
          gallery.scrollLeft = to;
        }
      }
    }
    $$('[data-gallery-thumb]', gallery.parentNode).forEach(function (t, i) { t.classList.toggle('is-active', i === index); });
    $$('[data-gallery-dot]', gallery.parentNode).forEach(function (d, i) { d.classList.toggle('is-active', i === index); });
  }

  $$('[data-gallery]').forEach(function (gallery) {
    var wrap = gallery.parentNode;
    var slides = $$('.gallery__slide', gallery);

    $$('[data-gallery-thumb]', wrap).forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { showSlide(wrap, i); });
    });
    $$('[data-gallery-dot]', wrap).forEach(function (dot, i) {
      dot.addEventListener('click', function () { showSlide(wrap, i); });
    });

    // Keep the dots in step with a swipe.
    var ticking = false;
    gallery.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var index = Math.round(gallery.scrollLeft / gallery.clientWidth);
        $$('[data-gallery-dot]', wrap).forEach(function (d, i) { d.classList.toggle('is-active', i === index); });
        $$('[data-gallery-thumb]', wrap).forEach(function (t, i) { t.classList.toggle('is-active', i === index); });
        ticking = false;
      });
    }, { passive: true });

    if (slides.length) slides[0].classList.add('is-active');
  });


  /* ---------------------------------------------------------- dropdowns --
     A listbox that can actually be styled. A native <select> renders as the
     OS picker on iOS and ignores every token in this theme.

     Selection itself is left to whoever owns the dropdown; this only handles
     opening, closing and keyboard movement. */
  function dropdownClose(dd) {
    var list = $('[data-dropdown-list]', dd);
    var toggle = $('[data-dropdown-toggle]', dd);
    if (!list || list.hidden) return;
    list.hidden = true;
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  function dropdownCloseAll(except) {
    $$('[data-dropdown]').forEach(function (dd) {
      if (dd !== except) dropdownClose(dd);
    });
  }

  function dropdownOpen(dd) {
    var list = $('[data-dropdown-list]', dd);
    var toggle = $('[data-dropdown-toggle]', dd);
    if (!list) return;
    dropdownCloseAll(dd);
    list.hidden = false;
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    var selected = $('[aria-selected="true"]', list) || $('[role="option"]', list);
    if (selected) selected.focus();
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-dropdown-toggle]');
    if (toggle) {
      var dd = toggle.closest('[data-dropdown]');
      var list = $('[data-dropdown-list]', dd);
      if (list && list.hidden) dropdownOpen(dd);
      else dropdownClose(dd);
      return;
    }
    // A click on an option, or anywhere outside, closes.
    var inside = e.target.closest('[data-dropdown]');
    dropdownCloseAll(inside && !e.target.closest('[role="option"]') ? inside : null);
  });

  document.addEventListener('keydown', function (e) {
    var dd = e.target.closest('[data-dropdown]');
    if (!dd) return;
    var list = $('[data-dropdown-list]', dd);
    if (!list) return;

    if (e.key === 'Escape') {
      dropdownClose(dd);
      var toggle = $('[data-dropdown-toggle]', dd);
      if (toggle) toggle.focus();
      return;
    }

    if (list.hidden) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        dropdownOpen(dd);
      }
      return;
    }

    var options = $$('[role="option"]', list);
    var at = options.indexOf(document.activeElement);

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      var step = e.key === 'ArrowDown' ? 1 : -1;
      var next = options[(at + step + options.length) % options.length];
      if (next) next.focus();
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      var edge = e.key === 'Home' ? options[0] : options[options.length - 1];
      if (edge) edge.focus();
    } else if ((e.key === 'Enter' || e.key === ' ') && at > -1) {
      e.preventDefault();
      options[at].click();
    }
  });

  /* ------------------------------------------------ collection filter/sort */
  $$('[data-collection]').forEach(function (root) {
    var sectionId = root.getAttribute('data-section-id');
    var grid = $('[data-collection-grid]', root);
    if (!grid || !sectionId) return;

    function load(url, push) {
      grid.classList.add('is-loading');
      var target = new URL(url, window.location.origin);
      var fetchUrl = new URL(target.toString());
      fetchUrl.searchParams.set('section_id', sectionId);

      fetch(fetchUrl.toString())
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-collection-grid]');
          var toolbar = parsed.querySelector('[data-collection-toolbar]');
          if (fresh) grid.innerHTML = fresh.innerHTML;
          var currentToolbar = $('[data-collection-toolbar]', root);
          if (toolbar && currentToolbar) currentToolbar.innerHTML = toolbar.innerHTML;
          if (push) window.history.pushState({}, '', target.toString());
          announce(strings.filtersApplied || 'Produktlistan uppdaterades.');
          root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        })
        .catch(function () { window.location.href = url; })
        .finally(function () { grid.classList.remove('is-loading'); });
    }

    root.addEventListener('click', function (e) {
      var link = e.target.closest('[data-filter-link]');
      if (!link) return;
      e.preventDefault();
      load(link.href, true);
    });

    /* Sorting. Four choices, with direction split out into its own toggle, so
       "sort_by" is assembled from a group plus a direction rather than picked
       from a flat list of eight. "relevant" carries no sort_by at all. */
    function currentDirection() {
      var toggle = $('[data-sort-dir]', root);
      return toggle && toggle.getAttribute('data-direction') === 'descending'
        ? 'descending' : 'ascending';
    }

    function applySort(group, direction) {
      var url = new URL(window.location.href);
      if (group === 'relevant' || !group) {
        url.searchParams.delete('sort_by');
      } else if (group === 'best-selling') {
        url.searchParams.set('sort_by', 'best-selling');
      } else {
        url.searchParams.set('sort_by', group + '-' + direction);
      }
      url.searchParams.delete('page');
      load(url.toString(), true);
    }

    root.addEventListener('click', function (e) {
      var option = e.target.closest('[data-sort-key]');
      if (option && root.contains(option)) {
        applySort(option.getAttribute('data-sort-key'), currentDirection());
        return;
      }

      var dir = e.target.closest('[data-sort-dir]');
      if (dir && root.contains(dir)) {
        var next = dir.getAttribute('data-direction') === 'descending' ? 'ascending' : 'descending';
        var selected = $('[data-sort-key][aria-selected="true"]', root);
        applySort(selected ? selected.getAttribute('data-sort-key') : 'relevant', next);
      }
    });

    window.addEventListener('popstate', function () { load(window.location.href, false); });
  });

  /* -------------------------------------------------------- scroll reveal */
  /* Sections fade and rise slightly as they enter the viewport. The hidden
     start state lives behind .reveal-ready, added here, so if this script
     never runs, nothing is left invisible. */
  var revealObserver = null;

  function revealNow(el) {
    el.classList.add('is-revealed');
  }

  function markRevealTargets(scope) {
    var sections = $$('.shopify-section', scope || document).filter(function (s) {
      // The product page's sticky gallery column is easier to reason about
      // without an animated ancestor, and the header/footer live outside main.
      if (!s.closest('main')) return false;
      if ($('[data-product]', s)) return false;
      return !s.hasAttribute('data-reveal');
    });

    sections.forEach(function (s) {
      s.setAttribute('data-reveal', '');
      if (revealObserver) revealObserver.observe(s);
      else revealNow(s);
    });
  }

  if (!reduceMotion.matches && 'IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        revealNow(entry.target);
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
  }

  // Tag the targets first, then arm the CSS. Doing it the other way round can
  // paint the page once unstyled before the hidden state applies.
  markRevealTargets(document);
  document.documentElement.classList.add('reveal-ready');

  // Anything already in view on load should not wait for a scroll event.
  window.requestAnimationFrame(function () {
    $$('[data-reveal]').forEach(function (el) {
      var box = el.getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) revealNow(el);
    });
  });

  // Re-arm when the customizer swaps a section in.
  document.addEventListener('shopify:section:load', function (e) {
    markRevealTargets(e.target.parentNode || document);
    revealNow(e.target);
  });


  /* ------------------------------------------------------- card swatches --
     Picking a colour on a product card swaps the photo to that variant's
     image, points the card's links at that variant, and where colour is the
     only option, makes quick-add put that exact variant in the cart. */
  function selectCardSwatch(button) {
    var card = button.closest('[data-card]');
    if (!card) return;

    $$('[data-card-swatch]', card).forEach(function (b) {
      var on = b === button;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', String(on));
    });

    var src = button.getAttribute('data-image-src');
    var image = $('[data-card-image]', card);
    if (image && src && image.getAttribute('src') !== src) {
      var srcset = button.getAttribute('data-image-srcset');
      var alt = button.getAttribute('data-image-alt');
      image.classList.add('is-swapping');
      var swap = new Image();
      swap.onload = function () {
        image.src = src;
        if (srcset) image.srcset = srcset;
        if (alt !== null) image.alt = alt;
        image.classList.remove('is-swapping');
      };
      // A broken or slow image must not leave the card blank.
      swap.onerror = function () { image.classList.remove('is-swapping'); };
      swap.src = src;
    }

    var url = button.getAttribute('data-variant-url');
    if (url) {
      $$('[data-card-link]', card).forEach(function (link) { link.href = url; });
    }

    var input = $('[data-card-variant-input]', card);
    var id = button.getAttribute('data-variant-id');
    if (input && id) input.value = id;

    /* Variants of one product do not have to cost the same: a 2 pack and a
       6 pack are one product with two prices. The card opens showing the
       lowest, prefixed with "Från", so once a pick is made that has to become
       the price of the thing picked, or the card is quoting one variant while
       adding another. */
    var priceBox = $('[data-card-price]', card);
    if (!priceBox) return;

    var amount = button.getAttribute('data-variant-price');
    var target = $('[data-price-amount]', priceBox);
    if (target && amount) target.textContent = amount;

    // "Från" answers "which of these prices?", which a pick has now settled.
    var from = $('.price__from', priceBox);
    if (from) from.hidden = true;

    var was = $('.price__was', priceBox);
    if (was) {
      var compare = button.getAttribute('data-variant-compare');
      var wasAmount = $('[data-price-was]', was);
      if (compare && wasAmount) {
        wasAmount.textContent = compare;
        was.hidden = false;
      } else {
        was.hidden = true;
      }
    }
  }

  document.addEventListener('click', function (e) {
    var swatch = e.target.closest('[data-card-swatch]');
    if (!swatch) return;
    e.preventDefault();
    selectCardSwatch(swatch);
  });

  // Warm the image so the first pick does not flash.
  document.addEventListener('pointerenter', function (e) {
    var swatch = e.target.closest && e.target.closest('[data-card-swatch]');
    if (!swatch || swatch.dataset.preloaded) return;
    var src = swatch.getAttribute('data-image-src');
    if (!src) return;
    swatch.dataset.preloaded = '1';
    var img = new Image();
    img.src = src;
  }, true);

  /* -------------------------------------------------------- Produkter menu --
     The category menu under Produkter on desktop. A disclosure of links, not
     the listbox dropdown above: nothing here is being selected, every row is a
     place to go. It opens on click, closes on a second click, a click outside,
     Escape, or focus leaving it, and the arrow keys walk its links. The
     animation is CSS keyed off .is-open. Listeners are delegated to the
     document, so a header re-rendered by the theme editor needs no rebinding. */
  function navMenuLinks(menu) {
    return $$('[data-nav-menu-panel] a[href]', menu);
  }

  function navMenuSet(menu, open) {
    var toggle = $('[data-nav-menu-toggle]', menu);
    menu.classList.toggle('is-open', open);
    if (toggle) toggle.setAttribute('aria-expanded', String(open));
  }

  function navMenuCloseAll(except) {
    $$('[data-nav-menu].is-open').forEach(function (menu) {
      if (menu !== except) navMenuSet(menu, false);
    });
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-nav-menu-toggle]');
    if (toggle) {
      var menu = toggle.closest('[data-nav-menu]');
      var open = !menu.classList.contains('is-open');
      navMenuCloseAll(menu);
      navMenuSet(menu, open);
      return;
    }
    // A click on a link inside navigates away anyway; anywhere else closes.
    navMenuCloseAll(e.target.closest('[data-nav-menu]'));
  });

  document.addEventListener('keydown', function (e) {
    var menu = e.target.closest && e.target.closest('[data-nav-menu]');
    if (!menu) return;
    var toggle = $('[data-nav-menu-toggle]', menu);
    var open = menu.classList.contains('is-open');

    if (e.key === 'Escape' && open) {
      navMenuSet(menu, false);
      if (toggle) toggle.focus();
      return;
    }

    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    var links = navMenuLinks(menu);
    if (!links.length) return;
    e.preventDefault();

    if (e.target === toggle) {
      navMenuSet(menu, true);
      // Focus once the panel is visible; a hidden element cannot take focus.
      window.requestAnimationFrame(function () {
        (e.key === 'ArrowUp' ? links[links.length - 1] : links[0]).focus();
      });
      return;
    }

    var at = links.indexOf(document.activeElement);
    var nextAt = e.key === 'ArrowDown' ? at + 1 : at - 1;
    if (nextAt < 0) { if (toggle) toggle.focus(); return; }
    if (nextAt >= links.length) nextAt = links.length - 1;
    links[nextAt].focus();
  });

  // Tabbing past the last row, or back out above the toggle, closes the menu.
  document.addEventListener('focusout', function (e) {
    var menu = e.target.closest && e.target.closest('[data-nav-menu]');
    if (!menu || !menu.classList.contains('is-open')) return;
    if (e.relatedTarget && menu.contains(e.relatedTarget)) return;
    // A click outside is handled by the click listener; this is for focus.
    if (!e.relatedTarget) return;
    navMenuSet(menu, false);
  });

  // Selecting a category block in the theme editor opens the menu to show it.
  document.addEventListener('shopify:block:select', function (e) {
    var menu = e.target.closest && e.target.closest('[data-nav-menu]');
    if (menu) navMenuSet(menu, true);
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var menu = e.target.closest && e.target.closest('[data-nav-menu]');
    if (menu) navMenuSet(menu, false);
  });

  /* ------------------------------------------------------------------ rail --
     A row that scrolls sideways: the homepage categories and Utvalda
     produkter. Any section marked data-rail-section gets it. Touch scrolling is the
     browser's own and needs nothing from here. What this adds is everything a
     mouse lacks: arrows, drag to scroll, and the cues that say the row goes on,
     which are the progress bar and the fade on whichever edge has more past
     it. All of it stays hidden while the row fits, so nothing offers to scroll
     a row that cannot. */
  function initRail(section) {
    var track = $('[data-rail-track]', section);
    var viewport = $('[data-rail]', section);
    if (!track || !viewport || track.hasAttribute('data-rail-ready')) return;
    track.setAttribute('data-rail-ready', '');

    var nav = $('[data-rail-nav]', section);
    var prev = $('[data-rail-prev]', section);
    var next = $('[data-rail-next]', section);
    var progress = $('[data-rail-progress]', section);
    var thumb = $('[data-rail-thumb]', section);
    var reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var slack = 2;

    function update() {
      var max = track.scrollWidth - track.clientWidth;
      var overflowing = max > slack;
      var atStart = track.scrollLeft <= slack;
      var atEnd = track.scrollLeft >= max - slack;

      if (nav) nav.hidden = !overflowing;
      if (progress) progress.hidden = !overflowing;
      if (prev) prev.disabled = !overflowing || atStart;
      if (next) next.disabled = !overflowing || atEnd;
      viewport.classList.toggle('has-prev', overflowing && !atStart);
      viewport.classList.toggle('has-next', overflowing && !atEnd);

      if (thumb && overflowing) {
        /* The thumb is as wide a share of the bar as the visible part is of
           the row, and travels the rest. translateX percentages are of the
           thumb's own width, hence the 1 / share. */
        var share = track.clientWidth / track.scrollWidth;
        var ratio = track.scrollLeft / max;
        thumb.style.width = (share * 100) + '%';
        thumb.style.transform = 'translateX(' + (ratio * (1 / share - 1) * 100) + '%)';
      }
    }

    var queued = false;
    function queueUpdate() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () { queued = false; update(); });
    }

    // One item and its gap, so a click moves the row by exactly one item.
    function step() {
      var item = track.firstElementChild;
      if (!item) return track.clientWidth * 0.8;
      var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    function scrollToLeft(left) {
      if (reduceMotion) { track.scrollLeft = left; return; }
      track.scrollTo({ left: left, behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { scrollToLeft(track.scrollLeft - step()); });
    if (next) next.addEventListener('click', function () { scrollToLeft(track.scrollLeft + step()); });

    track.addEventListener('scroll', queueUpdate, { passive: true });
    window.addEventListener('resize', queueUpdate);
    if ('ResizeObserver' in window) new ResizeObserver(queueUpdate).observe(track);

    /* Drag to scroll, for a mouse only: a finger already scrolls natively and
       a pen can too. The drag counts only after a few pixels, so an ordinary
       click still opens the category. Once it has counted, the click that the
       browser fires on release is swallowed, or letting go over a tile would
       navigate to it. */
    var dragging = false;
    var moved = false;
    var startX = 0;
    var startScroll = 0;
    var pointerId = null;
    var swallowClick = false;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (track.scrollWidth - track.clientWidth <= slack) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      pointerId = e.pointerId;
    });

    track.addEventListener('pointermove', function (e) {
      if (!dragging || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 6) {
        moved = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(pointerId); } catch (err) { /* already released */ }
      }
      if (moved) {
        track.scrollLeft = startScroll - dx;
        e.preventDefault();
      }
    });

    function endDrag(e) {
      if (!dragging || (e && e.pointerId !== pointerId)) return;
      dragging = false;
      if (!moved) return;
      swallowClick = true;
      window.setTimeout(function () { swallowClick = false; }, 0);
      try { track.releasePointerCapture(pointerId); } catch (err) { /* already released */ }

      /* Snapping is off while dragging. Turning it straight back on makes the
         row jump to a tile edge, so ease to the nearest one first and only
         then hand snapping back. */
      var s = step();
      var max = track.scrollWidth - track.clientWidth;
      var target = Math.max(0, Math.min(max, Math.round(track.scrollLeft / s) * s));
      scrollToLeft(target);
      window.setTimeout(function () { track.classList.remove('is-dragging'); }, reduceMotion ? 0 : 360);
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    track.addEventListener('click', function (e) {
      if (!swallowClick) return;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    // Otherwise the browser starts dragging the link or its photo instead.
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });

    update();
  }

  $$('[data-rail-section]').forEach(initRail);

  document.addEventListener('shopify:section:load', function (e) {
    var section = $('[data-rail-section]', e.target);
    if (section) initRail(section);
  });

  // Picking a category block in the theme editor brings its tile into view.
  document.addEventListener('shopify:block:select', function (e) {
    if (!e.target.closest || !e.target.closest('[data-rail-track]')) return;
    e.target.scrollIntoView({ block: 'nearest', inline: 'start' });
  });

  /* --------------------------------------------- sticky buy bar + footer --
     The bar is fixed to the bottom, so once the footer scrolls in it covers
     the last of it. Hide it while the footer is on screen. */
  (function () {
    var bar = $('[data-sticky-atc]');
    var footer = $('.footer');
    if (!bar || !footer || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-at-footer', entries[0].isIntersecting);
    }, { rootMargin: '0px 0px -8px 0px' }).observe(footer);
  })();


  /* --------------------------------------------------------- review pager --
     All reviews are server rendered; this only shows a window of them. Nothing
     is fetched, so paging is instant, and with JavaScript off every review is
     still on the page. */
  $$('[data-review-grid]').forEach(function (grid) {
    var items = $$('[data-review]', grid);
    var perPage = parseInt(grid.getAttribute('data-per-page'), 10) || 8;
    var section = grid.closest('section') || grid.parentNode;
    var pager = $('[data-review-pager]', section);
    if (!pager || items.length <= perPage) return;

    var pages = Math.ceil(items.length / perPage);
    var pagesBox = $('[data-review-pages]', pager);
    var prev = $('[data-review-prev]', pager);
    var next = $('[data-review-next]', pager);
    var current = 1;

    function pageButton(n) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'review-pager__page';
      b.textContent = n;
      b.setAttribute('aria-current', n === current ? 'true' : 'false');
      b.addEventListener('click', function () { show(n, true); });
      return b;
    }

    function gap() {
      var s = document.createElement('span');
      s.className = 'review-pager__gap';
      s.textContent = '...';
      return s;
    }

    // First, last, and a window around the current page. Keeps the row short
    // once there are a dozen pages.
    function paintPager() {
      pagesBox.innerHTML = '';
      var shown = [];
      for (var n = 1; n <= pages; n++) {
        if (n === 1 || n === pages || Math.abs(n - current) <= 1) shown.push(n);
      }
      shown.forEach(function (n, i) {
        if (i > 0 && n - shown[i - 1] > 1) pagesBox.appendChild(gap());
        pagesBox.appendChild(pageButton(n));
      });
      prev.disabled = current === 1;
      next.disabled = current === pages;
    }

    function show(n, moveFocus) {
      current = Math.min(Math.max(n, 1), pages);
      var start = (current - 1) * perPage;
      var end = start + perPage;

      grid.classList.add('is-turning');
      window.requestAnimationFrame(function () {
        items.forEach(function (item, i) { item.hidden = i < start || i >= end; });
        window.requestAnimationFrame(function () { grid.classList.remove('is-turning'); });
      });

      paintPager();

      if (moveFocus) {
        // Land on the heading rather than leaving the reader at the pager with
        // a page of reviews they have scrolled past.
        var head = $('.section-head__title', section);
        if (head) head.scrollIntoView({ behavior: 'smooth', block: 'start' });
        announce(strings.reviewsPage || '');
      }
    }

    prev.addEventListener('click', function () { show(current - 1, true); });
    next.addEventListener('click', function () { show(current + 1, true); });

    pager.hidden = false;
    show(1, false);
  });


  /* Both run once the whole file has bound its listeners, which is why they
     sit at the bottom rather than beside the functions they call. */
  $$('[data-product]').forEach(function (root) { syncUnits(root, false); });

  // The drawer is in the layout on every page and the cart page arrives
  // rendered, so the gift check happens before the customer touches anything.
  enforceGifts();

  /* ---------------------------------------------------------- newsletter */
  // Shopify posts the newsletter form normally; this only surfaces the result
  // banner after the redirect back with ?customer_posted=true.
  if (window.location.search.indexOf('customer_posted=true') !== -1) {
    var success = $('[data-newsletter-success]');
    if (success) success.hidden = false;
  }
})();
